<script setup>
/* global axios */
// This local Russian-only module follows the existing Telegram screens.
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { ref, reactive, computed, watch, onBeforeUnmount, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import SettingsLayout from '../settings/SettingsLayout.vue';
import BaseSettingsHeader from '../settings/components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import PlacementRows from './PlacementRows.vue';
import PlacementPagination from './PlacementPagination.vue';
import PlacementFilters from './PlacementFilters.vue';
import ReceiptFiles from './ReceiptFiles.vue';
import {
  readFilters,
  readPage,
  listQuery,
  statusLabels,
  userLabel,
  periodLabel,
  moneyLabel,
  paymentDateLabel,
  paymentTotalLabel,
} from './list';
import successImage from './placement-success.png';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import {
  packages,
  emptyForm,
  endDate,
  payload,
  validPaymentDate,
} from './form';

const route = useRoute();
const router = useRouter();
const tabs = [
  { label: 'Мои аккаунты', key: 'owned' },
  { label: 'Создать размещение', key: 'create' },
  { label: 'Список размещений', key: 'paid' },
];
// The sidebar links to the route without a query, always opening the form.
const tab = computed(() =>
  ['owned', 'paid'].includes(route.query?.view) ? route.query.view : 'create'
);
const groups = ref([]);
const groupPage = ref(1);
const groupTotal = ref(0);
const groupLoading = ref(false);
const groupError = ref('');
const rows = ref([]);
const loading = ref(false);
const error = ref('');
const denied = ref(false);
const unavailable = ref(false);
const saving = ref(false);
const uncertain = ref(false);
const formError = ref('');
const notice = ref('');
const form = reactive(emptyForm());
const receiptFiles = ref(null);
const createdGrantId = ref(null);
const chosenDays = ref(null);
const errorElement = ref(null);
const successDialog = ref(null);
let generation = 0;
let listSequence = 0;
let pending = null;
let requests = new AbortController();
const groupOptions = computed(() =>
  groups.value.map(group => ({
    value: group.id,
    label: group.title || 'Без названия',
  }))
);
const groupPagination = computed(
  () => `${groupPage.value} / ${Math.ceil(groupTotal.value / 25)}`
);
const selectedGroup = computed(() =>
  groups.value.find(group => group.id === Number(form.group))
);
const historyDialog = ref(null);
const historyTarget = ref(null);
const historyRows = ref([]);
const historyCursor = ref(null);
const historyLoading = ref(false);
const historyError = ref('');
let historySequence = 0;
const identityDialog = ref(null);
const identityTarget = ref(null);
const identityValue = ref('');
const identitySaving = ref(false);
const identityUncertain = ref(false);
const identityError = ref('');
let identityPending = null;
const locked = computed(
  () =>
    saving.value ||
    createdGrantId.value !== null ||
    uncertain.value ||
    identitySaving.value ||
    identityUncertain.value
);
const publicationReasons = {
  placement_priority: 'Размещение: публикация разрешена, списана 1 публикация',
  counted: 'Публикация учтена',
  no_access: 'Разрешение не применено',
  no_permission: 'Нет разрешения',
  limit_reached: 'Лимит исчерпан',
  revoked: 'Размещение отключено',
  not_started: 'Период ещё не начался',
  expired: 'Период закончился',
  identity_conflict: 'Конфликт пользователя — требуется подтверждение',
  identity_unconfirmed: 'Пользователь ещё не подтверждён',
};
const statuses = statusLabels;
const total = ref(0);
const pageSize = 25;
const currentPage = computed(() => readPage(route.query || {}));
const totalPages = computed(() =>
  Math.max(1, Math.ceil(total.value / pageSize))
);
const listStart = ref(null);
let scrollAfterLoad = false;
const payments = ref(null);
const paymentDialog = ref(null);
const paymentTarget = ref(null);
const paymentValue = ref('');
const paymentSaving = ref(false);
const paymentUncertain = ref(false);
const paymentError = ref('');
let paymentPending = null;
function resetPayment() {
  payments.value = null;
  paymentTarget.value = null;
  paymentValue.value = '';
  paymentPending = null;
  paymentSaving.value = false;
  paymentUncertain.value = false;
  paymentError.value = '';
  paymentDialog.value?.close();
}
const draftFilters = ref(readFilters(route.query || {}));
const filterDialog = ref(null);
const detailsDialog = ref(null);
const detailsTarget = ref(null);
const filterGroups = ref([]);
const filterGroupCursor = ref(null);
const filterGroupLoading = ref(false);
const filterGroupError = ref('');
let groupSearch = '';
let groupSearchTimer;
let filterGroupSequence = 0;
const appliedFilters = computed(() => readFilters(route.query || {}));
const activeFilterCount = computed(
  () => Object.values(appliedFilters.value).filter(Boolean).length
);
const filterOptions = computed(() => [
  { value: '', label: 'Все группы' },
  ...filterGroups.value.map(group => ({
    value: String(group.id),
    label: group.title || 'Без названия',
  })),
]);
const revokeDialog = ref(null);
const revokeTarget = ref(null);
const revokeSaving = ref(false);
const revokeUncertain = ref(false);
const revokeError = ref('');
let revokePending = null;
const listLocked = computed(
  () =>
    locked.value ||
    revokeSaving.value ||
    revokeUncertain.value ||
    paymentSaving.value ||
    paymentUncertain.value
);
const messages = {
  invalid_payment_date: 'Укажите корректную дату оплаты (2000–2100).',
  overlapping_permission:
    'Для этого пользователя уже есть размещение с пересекающимся периодом.',
  overlap:
    'Для этого пользователя уже есть размещение с пересекающимся периодом.',
  revision_conflict:
    'Размещение изменилось. Обновите список и повторите подтверждение.',
  identity_immutable: 'Пользователь уже определён. Изменить его нельзя.',
  search_timeout:
    'Поиск занял слишком много времени. Уточните группу или период.',
  invalid_query: 'Введите от 1 до 100 символов для поиска.',
  invalid_period:
    'Проверьте даты: начало должно быть не позже окончания (2000–2100).',
  invalid_group: 'Выберите группу из списка.',
  invalid_status: 'Выберите статус из списка.',
  invalid_cursor: 'Ссылка на страницу устарела. Нажмите «В начало».',
  invalid_page: 'Неверный номер страницы.',
  invalid_username: 'Проверьте @username.',
  identity_required: 'Укажите @username или TG User ID.',
  request_key_conflict:
    'Не удалось подтвердить повтор операции. Обновите список перед дальнейшими действиями.',
};
const message = e =>
  messages[e.response?.data?.error] ||
  (e.response?.status === 404
    ? 'Раздел пока недоступен или выбранная группа удалена.'
    : 'Не удалось выполнить запрос. Попробуйте ещё раз.');
async function request(path, method = 'get', data = undefined) {
  const current = generation;
  try {
    return (
      await axios({
        url: `/api/v1/accounts/${route.params.accountId}/${path}`,
        method,
        data,
        timeout: 20000,
        signal: requests.signal,
      })
    ).data;
  } catch (e) {
    if (current === generation && [401, 403].includes(e.response?.status)) {
      denied.value = true;
      createdGrantId.value = null;
      receiptFiles.value?.reset();
      resetPayment();
      rows.value = [];
      groups.value = [];
      pending = null;
      identityPending = null;
      identityValue.value = '';
      identityUncertain.value = false;
      historyRows.value = [];
      total.value = 0;
      filterGroups.value = [];
      detailsTarget.value = null;
      revokeTarget.value = null;
      revokePending = null;
      revokeUncertain.value = false;
      detailsDialog.value?.close();
      revokeDialog.value?.close();
      filterDialog.value?.close();
      historyDialog.value?.close();
      identityDialog.value?.close();
      Object.assign(form, emptyForm());
    }
    throw e;
  }
}
async function loadGroups(page = 1) {
  const current = generation;
  groupLoading.value = true;
  groupError.value = '';
  try {
    const data = await request(`tg_groups?page=${page}`);
    if (current !== generation) return;
    groups.value = data.items;
    groupPage.value = data.page;
    groupTotal.value = data.total;
    form.group = '';
  } catch (e) {
    if (current === generation) groupError.value = message(e);
  } finally {
    if (current === generation) groupLoading.value = false;
  }
}
async function loadFilterGroups(before = null) {
  const current = generation;
  filterGroupSequence += 1;
  const sequence = filterGroupSequence;
  filterGroupLoading.value = true;
  filterGroupError.value = '';
  try {
    const query = new URLSearchParams();
    if (groupSearch) query.set('query', groupSearch);
    if (before) query.set('before', before);
    if (draftFilters.value.group_id)
      query.set('selected', draftFilters.value.group_id);
    const data = await request(`tg_placements/groups?${query}`);
    if (current !== generation || sequence !== filterGroupSequence) return;
    filterGroups.value = [
      ...new Map(
        [...(data.selected ? [data.selected] : []), ...data.items].map(
          group => [group.id, group]
        )
      ).values(),
    ];
    filterGroupCursor.value = data.next_cursor;
  } catch (e) {
    if (current === generation && sequence === filterGroupSequence)
      filterGroupError.value = message(e);
  } finally {
    if (current === generation && sequence === filterGroupSequence)
      filterGroupLoading.value = false;
  }
}
function searchGroups(value) {
  groupSearch = String(value || '')
    .trim()
    .slice(0, 100);
  clearTimeout(groupSearchTimer);
  groupSearchTimer = setTimeout(() => loadFilterGroups(), 300);
}
function applyFilters(reset = false) {
  if (listLocked.value) return;
  filterDialog.value?.close();
  router.push({ query: listQuery(reset ? {} : draftFilters.value) });
}
function changePage(page) {
  if (loading.value || listLocked.value || page === currentPage.value) return;
  scrollAfterLoad = true;
  router.push({
    query: listQuery(
      appliedFilters.value,
      page,
      tab.value === 'owned' ? 'owned' : 'paid'
    ),
  });
}
async function loadRows(page = currentPage.value) {
  const current = generation;
  listSequence += 1;
  const sequence = listSequence;
  loading.value = true;
  error.value = '';
  rows.value = [];
  total.value = 0;
  payments.value = null;
  try {
    const query = new URLSearchParams(
      tab.value === 'paid'
        ? listQuery(appliedFilters.value, page)
        : listQuery({}, page, 'owned')
    );
    query.delete('view');
    query.set('kind', tab.value === 'owned' ? 'owned' : 'paid');
    query.set('page', String(page));
    const endpoint =
      tab.value === 'owned' ? 'tg_placements/owned_accounts' : 'tg_placements';
    const data = await request(`${endpoint}?${query}`);
    if (current !== generation || sequence !== listSequence) return;
    rows.value = data.items;
    total.value = data.total ?? data.items.length;
    payments.value = data.payments || null;
    unavailable.value = false;
    const resolvedPage = Number(data.page || page);
    if (resolvedPage !== currentPage.value) {
      await router.replace({
        query: listQuery(
          appliedFilters.value,
          resolvedPage,
          tab.value === 'owned' ? 'owned' : 'paid'
        ),
      });
      return;
    }
    if (scrollAfterLoad) {
      scrollAfterLoad = false;
      await nextTick();
      listStart.value?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    }
  } catch (e) {
    if (current !== generation || sequence !== listSequence) return;
    unavailable.value = e.response?.status === 404;
    error.value = message(e);
  } finally {
    if (current === generation && sequence === listSequence)
      loading.value = false;
  }
}
function openPayment(row) {
  detailsDialog.value?.close();
  if (!paymentPending) {
    paymentTarget.value = row;
    paymentValue.value = row.payment_date || '';
    paymentError.value = '';
  }
  paymentDialog.value?.open();
}
async function confirmPayment() {
  if (paymentSaving.value || denied.value) return;
  paymentError.value = '';
  if (!paymentPending) {
    if (!validPaymentDate(paymentValue.value)) {
      paymentError.value = 'Укажите корректную дату оплаты.';
      return;
    }
    paymentPending = {
      payment_date: paymentValue.value,
      lock_version: paymentTarget.value.lock_version,
      request_key: crypto.randomUUID(),
      reason: 'Уточнение даты оплаты администратором',
    };
  }
  const current = generation;
  paymentSaving.value = true;
  try {
    await request(
      `tg_placements/${paymentTarget.value.id}`,
      'patch',
      paymentPending
    );
    if (current !== generation) return;
    resetPayment();
    notice.value = 'Дата оплаты сохранена.';
    await loadRows();
  } catch (e) {
    if (current !== generation || denied.value) return;
    paymentUncertain.value =
      !e.response ||
      e.response.status >= 500 ||
      [408, 429].includes(e.response.status) ||
      e.response?.data?.error === 'request_key_conflict';
    if (!paymentUncertain.value) paymentPending = null;
    paymentError.value = paymentUncertain.value
      ? 'Ответ не получен. Нажмите «Проверить дату оплаты».'
      : message(e);
  } finally {
    if (current === generation) paymentSaving.value = false;
  }
}
function receiptSaved({ grantId, receipt }) {
  const row = rows.value.find(item => String(item.id) === String(grantId));
  if (row)
    row.receipts = [
      ...(row.receipts || []).filter(item => item.id !== receipt.id),
      receipt,
    ];
  if (detailsTarget.value && String(detailsTarget.value.id) === String(grantId))
    detailsTarget.value.receipts = row?.receipts || [
      ...(detailsTarget.value.receipts || []).filter(
        item => item.id !== receipt.id
      ),
      receipt,
    ];
}
function receiptDenied() {
  generation += 1;
  requests.abort();
  denied.value = true;
  rows.value = [];
  groups.value = [];
  createdGrantId.value = null;
  receiptFiles.value?.reset();
}
function openDetails(row) {
  detailsTarget.value = row;
  detailsDialog.value?.open();
}
function openRevoke(row) {
  detailsDialog.value?.close();
  if (!revokePending) {
    revokeTarget.value = row;
    revokeError.value = '';
  }
  revokeDialog.value?.open();
}
async function confirmRevoke() {
  if (revokeSaving.value || denied.value) return;
  if (!revokePending)
    revokePending = {
      lock_version: revokeTarget.value.lock_version,
      request_key: crypto.randomUUID(),
      reason: 'Отключение размещения администратором',
    };
  const current = generation;
  revokeSaving.value = true;
  revokeError.value = '';
  try {
    await request(
      `tg_placements/${revokeTarget.value.id}/revoke`,
      'post',
      revokePending
    );
    if (current !== generation) return;
    revokePending = null;
    revokeUncertain.value = false;
    revokeDialog.value?.close();
    notice.value = 'Размещение отключено. История сохранена.';
    await loadRows();
  } catch (e) {
    if (current !== generation || denied.value) return;
    revokeUncertain.value =
      !e.response ||
      e.response.status >= 500 ||
      [408, 429].includes(e.response.status) ||
      e.response?.data?.error === 'request_key_conflict';
    if (!revokeUncertain.value) revokePending = null;
    revokeError.value = revokeUncertain.value
      ? 'Ответ не получен. Нажмите «Проверить отключение».'
      : message(e);
  } finally {
    if (current === generation) revokeSaving.value = false;
  }
}

async function loadHistory(before = null) {
  const current = generation;
  historySequence += 1;
  const sequence = historySequence;
  const id = historyTarget.value?.id;
  if (!id) return;
  historyLoading.value = true;
  historyError.value = '';
  historyRows.value = [];
  historyCursor.value = null;
  try {
    const data = await request(
      `tg_placements/${id}/publications${before ? `?before=${before}` : ''}`
    );
    if (current !== generation || sequence !== historySequence) return;
    historyRows.value = data.items;
    historyCursor.value = data.next_cursor;
  } catch (e) {
    if (current === generation && sequence === historySequence)
      historyError.value = message(e);
  } finally {
    if (current === generation && sequence === historySequence)
      historyLoading.value = false;
  }
}
async function openHistory(row) {
  detailsDialog.value?.close();
  historyTarget.value = row;
  historyDialog.value?.open();
  await loadHistory();
}
function openIdentity(row) {
  detailsDialog.value?.close();
  if (!identityPending) {
    identityTarget.value = row;
    identityValue.value = '';
    identityError.value = '';
  }
  identityDialog.value?.open();
}
async function confirmIdentity() {
  if (identitySaving.value || denied.value) return;
  identityError.value = '';
  if (!identityPending) {
    const value = identityValue.value.trim();
    if (
      !/^[1-9]\d*$/.test(value) ||
      !Number.isSafeInteger(Number(value)) ||
      Number(value) > 4503599627370495
    ) {
      identityError.value = 'Укажите корректный TG User ID.';
      return;
    }
    identityPending = {
      telegram_user_id: Number(value),
      lock_version: identityTarget.value.lock_version,
      request_key: crypto.randomUUID(),
      reason: 'Подтверждение пользователя администратором',
    };
  }
  const current = generation;
  const id = identityTarget.value.id;
  identitySaving.value = true;
  try {
    await request(`tg_placements/${id}`, 'patch', identityPending);
    if (current !== generation) return;
    identityPending = null;
    identityUncertain.value = false;
    identityDialog.value?.close();
    notice.value = 'Пользователь подтверждён.';
    await loadRows();
  } catch (e) {
    if (current !== generation || denied.value) return;
    identityUncertain.value =
      !e.response ||
      e.response.status >= 500 ||
      [408, 429].includes(e.response.status) ||
      e.response?.data?.error === 'request_key_conflict';
    if (!identityUncertain.value) identityPending = null;
    identityError.value = identityUncertain.value
      ? 'Ответ не получен. Нажмите «Проверить сохранение».'
      : message(e);
  } finally {
    if (current === generation) identitySaving.value = false;
  }
}
function choosePeriod(days) {
  chosenDays.value = days;
  form.to = endDate(form.from, days);
}
watch(
  () => form.from,
  () => {
    if (chosenDays.value) form.to = endDate(form.from, chosenDays.value);
  }
);
function choosePackage(item) {
  form.limit = item.count;
  form.amount = item.price;
}
async function focusError() {
  await nextTick();
  errorElement.value?.focus();
}
async function submit() {
  if (
    paymentSaving.value ||
    paymentUncertain.value ||
    revokeSaving.value ||
    revokeUncertain.value ||
    saving.value ||
    identitySaving.value ||
    identityUncertain.value ||
    denied.value ||
    unavailable.value
  )
    return;
  formError.value = '';
  notice.value = '';
  if (!pending) {
    try {
      pending = { ...payload(form), request_key: crypto.randomUUID() };
    } catch (e) {
      formError.value = e.message;
      await focusError();
      return;
    }
  }
  const current = generation;
  saving.value = true;
  try {
    if (!createdGrantId.value) {
      const result = await request('tg_placements', 'post', pending);
      if (current !== generation) return;
      if (receiptFiles.value?.hasFiles()) {
        createdGrantId.value = result.ids[0];
      }
    }
    if (createdGrantId.value) {
      const complete = await receiptFiles.value.upload(createdGrantId.value);
      if (current !== generation) return;
      if (!complete) {
        uncertain.value = false;
        formError.value =
          'Размещение уже создано. Не все чеки загружены: повторите загрузку или уберите файл с ошибкой. Повторное размещение не создастся.';
        await focusError();
        return;
      }
    }
    createdGrantId.value = null;
    receiptFiles.value?.reset();
    notice.value = pending.telegram_user_id
      ? 'Размещение создано.'
      : 'Размещение сохранено.';
    pending = null;
    uncertain.value = false;
    chosenDays.value = null;
    Object.assign(form, emptyForm());
    await nextTick();
    if (current !== generation) return;
    successDialog.value?.open();
    await loadRows();
  } catch (e) {
    if (current !== generation || denied.value) return;
    // A timeout/5xx can happen after commit. Freeze the exact request and reuse its UUID.
    uncertain.value =
      !e.response ||
      e.response.status >= 500 ||
      [408, 429].includes(e.response.status) ||
      e.response?.data?.error === 'request_key_conflict';
    if (!uncertain.value) pending = null;
    formError.value = uncertain.value
      ? 'Ответ о сохранении не получен. Нажмите «Проверить сохранение»: повтор с теми же данными не создаст дубль.'
      : message(e);
    await focusError();
  } finally {
    if (current === generation) saving.value = false;
  }
}
function switchTab(value) {
  if (listLocked.value) return;
  router.push({
    name: 'telegram_placements',
    params: { accountId: route.params.accountId },
    query: value.key === 'create' ? {} : { view: value.key },
  });
}
watch(
  () => route.query,
  () => {
    notice.value = '';
    draftFilters.value = readFilters(route.query || {});
    loadRows();
    if (tab.value === 'paid') loadFilterGroups();
  }
);
function dateLabel(value, last = false) {
  return new Intl.DateTimeFormat('ru-RU', { timeZone: 'Europe/Moscow' }).format(
    new Date(new Date(value).getTime() - (last ? 1 : 0))
  );
}
watch(
  () => route.params.accountId,
  (_account, previousAccount) => {
    resetPayment();
    receiptFiles.value?.reset();
    createdGrantId.value = null;
    if (previousAccount)
      router.push({ query: tab.value === 'create' ? {} : { view: tab.value } });
    clearTimeout(groupSearchTimer);
    filterGroupSequence += 1;
    filterGroups.value = [];
    filterGroupCursor.value = null;
    filterGroupLoading.value = false;
    filterGroupError.value = '';
    groupSearch = '';
    draftFilters.value = readFilters(route.query || {});
    detailsTarget.value = null;
    revokeTarget.value = null;
    revokePending = null;
    revokeSaving.value = false;
    revokeUncertain.value = false;
    revokeError.value = '';
    detailsDialog.value?.close();
    revokeDialog.value?.close();
    filterDialog.value?.close();
    successDialog.value?.close();
    historyDialog.value?.close();
    identityDialog.value?.close();
    historySequence += 1;
    historyTarget.value = null;
    historyRows.value = [];
    historyCursor.value = null;
    historyLoading.value = false;
    identityTarget.value = null;
    identityValue.value = '';
    identityPending = null;
    identitySaving.value = false;
    identityUncertain.value = false;
    identityError.value = '';
    generation += 1;
    requests.abort();
    requests = new AbortController();
    pending = null;
    denied.value = false;
    unavailable.value = false;
    saving.value = false;
    uncertain.value = false;
    notice.value = '';
    formError.value = '';
    chosenDays.value = null;
    rows.value = [];
    groups.value = [];
    Object.assign(form, emptyForm());
    loadRows();
    loadGroups();
    if (tab.value === 'paid') loadFilterGroups();
  },
  { immediate: true }
);
onBeforeUnmount(() => {
  clearTimeout(groupSearchTimer);
  generation += 1;
  requests.abort();
});
</script>

<template>
  <SettingsLayout class="placements-page min-w-0">
    <template #header>
      <BaseSettingsHeader
        title="Размещения"
        description="Разрешения на публикации в Telegram-группах"
      />
      <div
        class="max-w-full overflow-x-auto py-1"
        :inert="listLocked || undefined"
      >
        <TabBar
          :tabs="tabs"
          :initial-active-tab="tabs.findIndex(item => item.key === tab)"
          class="min-w-max"
          @tab-changed="switchTab"
        />
      </div>
    </template>
    <template #body>
      <Banner v-if="denied" color="ruby" role="alert">
        Нет доступа. Раздел доступен администратору рабочей области.
      </Banner>
      <template v-else>
        <Banner v-if="paymentUncertain" color="amber" class="mb-6">
          Проверьте результат сохранения даты оплаты.
          <Button
            type="button"
            label="Проверить дату оплаты"
            @click="paymentDialog?.open()"
          />
        </Banner>
        <Banner v-if="identityUncertain" color="amber" class="mb-6">
          Подтвердите результат сохранения пользователя.
          <Button
            type="button"
            label="Проверить сохранение"
            @click="identityDialog?.open()"
          />
        </Banner>
        <Banner v-if="notice" color="teal" role="status" class="mb-6">
          {{ notice }}
        </Banner>
        <Banner v-if="unavailable" color="amber" class="mb-6">
          Раздел размещений пока не включён. Создание недоступно.
        </Banner>
        <form v-if="tab === 'create'" class="max-w-xl" @submit.prevent="submit">
          <h2 class="text-lg font-medium text-n-slate-12 mb-6">
            Создать платное размещение
          </h2>
          <fieldset
            :disabled="listLocked || unavailable"
            class="flex flex-col gap-5 min-w-0 m-0 p-0 border-0"
          >
            <div class="flex flex-col gap-2 min-w-0">
              <label
                id="placement-group-label"
                class="text-sm font-medium text-n-slate-12"
              >
                <span>Группа</span>
              </label>
              <ComboBox
                v-model="form.group"
                :options="groupOptions"
                :disabled="locked || unavailable || groupLoading"
                placeholder="Выберите группу"
                search-placeholder="Поиск на этой странице"
                empty-state="Группы не найдены"
                aria-labelledby="placement-group-label"
              />
              <p
                v-if="selectedGroup"
                class="text-sm text-n-slate-11 break-words m-0"
              >
                {{ selectedGroup.title }}
              </p>
              <p
                v-if="groupLoading"
                role="status"
                class="text-sm text-n-slate-11 m-0"
              >
                Загрузка групп…
              </p>
              <div
                v-else-if="groupError"
                role="alert"
                class="text-sm text-n-ruby-9"
              >
                {{ groupError }}
                <Button
                  type="button"
                  label="Повторить"
                  slate
                  link
                  @click="loadGroups(groupPage)"
                />
              </div>
              <p v-else-if="!groupTotal" class="text-sm text-n-slate-11 m-0">
                Сначала добавьте группу в «Настройки → ТГ группы».
              </p>
              <div
                v-if="groupTotal > 25"
                class="flex flex-wrap items-center gap-2"
              >
                <Button
                  type="button"
                  label="Назад"
                  slate
                  faded
                  :disabled="groupLoading || groupPage === 1"
                  @click="loadGroups(groupPage - 1)"
                />
                <span class="text-sm">{{ groupPagination }}</span>
                <Button
                  type="button"
                  label="Далее"
                  slate
                  faded
                  :disabled="groupLoading || groupPage * 25 >= groupTotal"
                  @click="loadGroups(groupPage + 1)"
                />
              </div>
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                id="placement-username"
                v-model="form.username"
                label="@username"
              />
              <Input
                id="placement-user-id"
                v-model="form.userId"
                label="TG User ID"
              />
            </div>
            <p class="text-sm text-n-slate-11 -mt-3 mb-0">
              Заполните хотя бы одно поле. Можно оба. По username пользователь
              определится при первой публикации в выбранной группе.
            </p>
            <div class="flex flex-col gap-2">
              <Input
                id="placement-from"
                v-model="form.from"
                type="date"
                label="Период с"
                min="2000-01-01"
                max="2100-12-31"
              />
              <div
                class="flex flex-wrap gap-2"
                role="group"
                aria-label="Длительность размещения"
              >
                <Button
                  v-for="days in [7, 14, 30]"
                  :key="days"
                  type="button"
                  slate
                  faded
                  class="min-h-10"
                  :label="`${days} дней`"
                  :aria-pressed="chosenDays === days"
                  @click="choosePeriod(days)"
                />
              </div>
            </div>
            <Input
              id="placement-to"
              v-model="form.to"
              type="date"
              label="Период до"
              :min="form.from"
              max="2100-12-31"
              message="Включительно · московское время"
              @input="chosenDays = null"
            />
            <div class="flex flex-col gap-2">
              <Input
                id="placement-limit"
                v-model="form.limit"
                type="number"
                label="Лимит публикаций"
                min="1"
                max="1000000"
              />
              <div
                class="flex flex-wrap gap-2"
                role="group"
                aria-label="Количество публикаций"
              >
                <Button
                  v-for="item in packages"
                  :key="item.count"
                  type="button"
                  slate
                  faded
                  class="min-h-10"
                  :label="`${item.count} шт`"
                  @click="choosePackage(item)"
                />
              </div>
            </div>
            <Input
              id="placement-amount"
              v-model="form.amount"
              label="Оплатил, ₽"
              placeholder="0"
              message="Сумму можно изменить вручную"
            />
            <Input
              id="placement-payment-date"
              v-model="form.paymentDate"
              type="date"
              label="Дата оплаты"
              :disabled="
                String(form.amount).trim() !== '' &&
                Number(String(form.amount).replace(',', '.')) === 0
              "
              message="Когда получены деньги. Не зависит от срока размещения."
            />
            <TextArea
              id="placement-notes"
              v-model="form.notes"
              label="Примечание"
              placeholder="Необязательно"
              :max-length="1000"
              resize
            />
          </fieldset>
          <ReceiptFiles
            :key="route.params.accountId"
            ref="receiptFiles"
            class="mt-5"
            :account-id="route.params.accountId"
            :disabled="saving || uncertain || unavailable"
            @denied="receiptDenied"
          />
          <div
            v-if="formError"
            ref="errorElement"
            tabindex="-1"
            role="alert"
            class="mt-5 rounded-lg text-sm text-n-ruby-9 focus:outline focus:outline-n-brand"
          >
            {{ formError }}
          </div>
          <Button
            type="submit"
            class="mt-6 w-full sm:w-auto min-h-10"
            :label="
              createdGrantId
                ? 'Завершить загрузку чеков'
                : uncertain
                  ? 'Проверить сохранение'
                  : 'Создать размещение'
            "
            :is-loading="saving"
            :disabled="
              paymentSaving ||
              paymentUncertain ||
              revokeSaving ||
              revokeUncertain ||
              saving ||
              identitySaving ||
              identityUncertain ||
              loading ||
              unavailable ||
              groupLoading ||
              (!uncertain && !groupTotal)
            "
          />
        </form>
        <Banner v-if="tab === 'owned'" color="slate">
          Авторизованные личные аккаунты автоматически могут публиковаться во
          всех подключённых группах без оплаты и лимитов. Аккаунт должен
          состоять в группе и иметь право отправлять сообщения в Telegram.
        </Banner>
        <Banner v-if="revokeUncertain" color="amber" class="mb-4">
          Проверьте результат отключения размещения.
          <Button
            type="button"
            label="Проверить отключение"
            @click="revokeDialog?.open()"
          />
        </Banner>
        <section
          v-if="tab === 'paid'"
          ref="listStart"
          class="mt-4 min-w-0"
          aria-label="Список размещений"
        >
          <form
            class="flex flex-col gap-4 mb-6"
            @submit.prevent="applyFilters()"
          >
            <div class="flex flex-wrap items-end gap-3">
              <Input
                v-model="draftFilters.query"
                label="Поиск"
                placeholder="Username, ID или примечание"
                :maxlength="100"
                :disabled="listLocked"
                class="w-full sm:flex-1 sm:w-auto min-w-0"
              />
              <Button type="submit" label="Найти" :disabled="listLocked" />
              <Button
                type="button"
                :label="`Фильтры${activeFilterCount ? ` (${activeFilterCount})` : ''}`"
                slate
                faded
                class="lg:hidden"
                :disabled="listLocked"
                @click="filterDialog?.open()"
              />
            </div>
            <PlacementFilters
              v-model="draftFilters"
              class="hidden lg:grid"
              :groups="filterOptions"
              :loading="filterGroupLoading"
              :error="filterGroupError"
              :cursor="filterGroupCursor"
              :disabled="listLocked"
              @search="searchGroups"
              @more="loadFilterGroups(filterGroupCursor)"
            />
            <div class="flex flex-wrap gap-2">
              <Button
                type="submit"
                label="Применить фильтры"
                slate
                faded
                class="hidden lg:flex"
                :disabled="listLocked"
              />
              <Button
                v-if="activeFilterCount"
                type="button"
                label="Сбросить фильтры"
                slate
                ghost
                :disabled="listLocked"
                @click="applyFilters(true)"
              />
              <Button
                type="button"
                label="Обновить"
                slate
                ghost
                :disabled="loading || listLocked"
                @click="loadRows()"
              />
            </div>
          </form>
          <p v-if="loading" role="status" class="text-sm text-n-slate-11">
            Загрузка размещений…
          </p>
          <Banner v-else-if="error" color="ruby" role="alert">
            {{ error }}
            <Button
              type="button"
              label="Повторить"
              slate
              ghost
              @click="loadRows()"
            />
          </Banner>
          <template v-else>
            <p role="status" class="text-sm text-n-slate-11">
              Найдено: {{ total }} · Новые сверху · По 25 на странице
            </p>
            <div
              v-if="payments"
              class="text-sm text-n-slate-12 mb-4"
              role="status"
            >
              <p class="m-0">
                Сумма оплат по всей выборке:
                {{ paymentTotalLabel(payments.amount_minor) }} · Оплат:
                {{ payments.count }}
              </p>
              <p v-if="payments.undated_count" class="m-0 text-n-slate-11">
                Без даты оплаты: {{ payments.undated_count }}. В сумму не
                включены — укажите дату в подробностях размещения.
              </p>
            </div>
            <p v-if="!rows.length" class="text-sm text-n-slate-11">
              {{
                activeFilterCount
                  ? 'По выбранным фильтрам ничего не найдено.'
                  : 'Размещений пока нет.'
              }}
            </p>
            <PlacementRows
              v-else
              :rows="rows"
              :account-id="route.params.accountId"
              :locked="listLocked"
              @details="openDetails"
              @history="openHistory"
              @identity="openIdentity"
              @revoke="openRevoke"
              @payment="openPayment"
              @receipt="receiptSaved"
              @denied="receiptDenied"
            />
            <PlacementPagination
              v-if="rows.length && totalPages > 1"
              :current-page="currentPage"
              :total-items="total"
              :items-per-page="pageSize"
              :disabled="loading || listLocked"
              @change="changePage"
            />
          </template>
        </section>
        <section
          v-if="tab === 'owned'"
          data-feature="tg_automatic_owned_accounts"
          ref="listStart"
          class="mt-6"
          aria-label="Список размещений"
        >
          <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h2 class="text-lg font-medium text-n-slate-12 m-0">
              {{ tab === 'paid' ? 'Платные размещения' : 'Мои аккаунты' }}
            </h2>
            <Button
              type="button"
              label="Обновить"
              slate
              faded
              :disabled="loading || saving"
              @click="loadRows()"
            />
          </div>
          <p v-if="loading" role="status" class="text-sm text-n-slate-11">
            Загрузка аккаунтов…
          </p>
          <Banner v-else-if="error" color="ruby" role="alert">
            {{ error }}
          </Banner>
          <p v-else-if="!rows.length" class="text-sm text-n-slate-11">
            Подтверждённых аккаунтов пока нет. После успешной авторизации они
            появятся здесь автоматически.
          </p>
          <div v-else class="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <article
              v-for="row in rows"
              :key="row.id"
              class="p-4 border border-n-weak rounded-xl flex flex-col gap-2 min-w-0"
            >
              <div class="flex min-w-0 items-center gap-3">
                <Avatar
                  :src="row.avatar_url"
                  :name="row.name || 'Telegram'"
                  :size="40"
                />
                <div class="min-w-0">
                  <p class="m-0 font-medium text-n-slate-12 break-words">
                    {{ row.name || 'Telegram-аккаунт' }}
                  </p>
                  <p
                    v-if="row.username"
                    class="m-0 text-sm text-n-slate-11 break-all"
                  >
                    @{{ row.username }}
                  </p>
                </div>
              </div>
              <p class="text-sm text-n-slate-11 m-0 break-all">
                Telegram ID: {{ row.telegram_user_id }}
              </p>
              <p class="text-sm text-n-teal-11 m-0">
                Разрешено во всех группах · без лимитов
              </p>
            </article>
          </div>
          <PlacementPagination
            v-if="!loading && rows.length && totalPages > 1"
            :current-page="currentPage"
            :total-items="total"
            :items-per-page="pageSize"
            :disabled="listLocked"
            @change="changePage"
          />
        </section>
        <Dialog
          ref="filterDialog"
          title="Фильтры размещений"
          confirm-button-label="Применить"
          cancel-button-label="Закрыть"
          overflow-y-auto
          :disable-confirm-button="listLocked"
          @confirm="applyFilters()"
        >
          <PlacementFilters
            v-model="draftFilters"
            :groups="filterOptions"
            :loading="filterGroupLoading"
            :error="filterGroupError"
            :cursor="filterGroupCursor"
            :disabled="listLocked"
            @search="searchGroups"
            @more="loadFilterGroups(filterGroupCursor)"
          />
        </Dialog>
        <Dialog
          ref="detailsDialog"
          title="Размещение"
          confirm-button-label="Закрыть"
          :show-cancel-button="false"
          overflow-y-auto
          @confirm="detailsDialog?.close()"
        >
          <div
            v-if="detailsTarget"
            class="flex flex-col gap-3 text-sm text-n-slate-12 min-w-0"
          >
            <p class="m-0 break-all font-medium">
              {{ userLabel(detailsTarget) }}
            </p>
            <p v-if="detailsTarget.telegram_user_id" class="m-0">
              TG User ID: {{ detailsTarget.telegram_user_id }}
            </p>
            <p class="m-0 break-words">
              {{ detailsTarget.group_title || 'Группа недоступна' }}
            </p>
            <p class="m-0">{{ statuses[detailsTarget.status] }}</p>
            <p class="m-0">{{ periodLabel(detailsTarget) }}</p>
            <p class="m-0">
              Осталось {{ detailsTarget.remaining }} из
              {{ detailsTarget.limit_messages }} · Оплатил
              {{ moneyLabel(detailsTarget) }}
            </p>
            <p class="m-0">
              Дата оплаты: {{ paymentDateLabel(detailsTarget) }}
            </p>
            <Button
              v-if="detailsTarget.paid_amount_minor > 0"
              type="button"
              label="Изменить дату оплаты"
              slate
              faded
              :disabled="listLocked"
              @click="openPayment(detailsTarget)"
            />
            <ReceiptFiles
              :key="`${route.params.accountId}-${detailsTarget.id}`"
              :account-id="route.params.accountId"
              :grant-id="detailsTarget.id"
              :receipts="detailsTarget.receipts || []"
              :disabled="listLocked"
              @saved="receiptSaved"
              @denied="receiptDenied"
            />
            <p class="m-0 break-words whitespace-pre-wrap">
              {{ detailsTarget.notes || 'Примечание не указано.' }}
            </p>
            <Button
              type="button"
              label="История публикаций"
              slate
              faded
              :disabled="listLocked"
              @click="openHistory(detailsTarget)"
            />
            <Button
              v-if="
                !detailsTarget.telegram_user_id &&
                detailsTarget.status !== 'revoked'
              "
              type="button"
              label="Указать TG User ID"
              slate
              faded
              :disabled="listLocked"
              @click="openIdentity(detailsTarget)"
            />
            <Button
              v-if="detailsTarget.status !== 'revoked'"
              type="button"
              label="Отключить размещение"
              ruby
              faded
              :disabled="listLocked"
              @click="openRevoke(detailsTarget)"
            />
          </div>
        </Dialog>
        <Dialog
          ref="paymentDialog"
          title="Дата оплаты"
          width="sm"
          :confirm-button-label="
            paymentUncertain ? 'Проверить дату оплаты' : 'Сохранить дату оплаты'
          "
          cancel-button-label="Отмена"
          :is-loading="paymentSaving"
          @confirm="confirmPayment"
        >
          <p class="text-sm text-n-slate-11 m-0">
            Укажите дату получения денег. Срок и лимит размещения не изменятся.
          </p>
          <Input
            v-model="paymentValue"
            type="date"
            label="Дата оплаты"
            :disabled="paymentSaving || paymentUncertain"
          />
          <Banner v-if="paymentError" color="ruby" role="alert">
            {{ paymentError }}
          </Banner>
        </Dialog>
        <Dialog
          ref="revokeDialog"
          title="Отключить размещение?"
          :confirm-button-label="
            revokeUncertain ? 'Проверить отключение' : 'Отключить'
          "
          cancel-button-label="Отмена"
          :is-loading="revokeSaving"
          @confirm="confirmRevoke"
        >
          <p class="text-sm text-n-slate-12 break-words">
            {{ revokeTarget ? userLabel(revokeTarget) : '' }} больше не сможет
            использовать это размещение для новых публикаций. История и
            использованный лимит сохранятся.
          </p>
          <Banner v-if="revokeError" color="ruby" role="alert">
            {{ revokeError }}
          </Banner>
        </Dialog>
        <Dialog
          ref="historyDialog"
          title="История публикаций"
          width="lg"
          :show-cancel-button="false"
          confirm-button-label="Закрыть"
          overflow-y-auto
          @confirm="historyDialog?.close()"
        >
          <p v-if="historyLoading" role="status">Загрузка истории…</p>
          <Banner v-else-if="historyError" color="ruby" role="alert">
            {{ historyError }}
          </Banner>
          <p v-else-if="!historyRows.length" class="text-sm text-n-slate-11">
            Учтённых публикаций пока нет.
          </p>
          <div v-else class="flex flex-col gap-3 max-h-[45vh] overflow-y-auto">
            <div
              v-for="item in historyRows"
              :key="item.id"
              class="border-b border-n-weak pb-3 text-sm break-words"
            >
              <p class="m-0 text-n-slate-12">
                {{
                  publicationReasons[item.reason || item.decision] ||
                  'Решение не указано'
                }}
              </p>
              <p class="m-0 text-n-slate-11">
                {{ dateLabel(item.published_at) }} · Сообщение
                {{ item.provider_message_id }}
              </p>
            </div>
          </div>
          <Button
            v-if="historyError"
            type="button"
            label="Повторить"
            slate
            faded
            @click="loadHistory()"
          />
          <Button
            v-if="historyCursor && !historyLoading"
            type="button"
            label="Следующие 25"
            slate
            faded
            @click="loadHistory(historyCursor)"
          />
        </Dialog>
        <Dialog
          ref="identityDialog"
          title="Подтвердить пользователя"
          width="sm"
          :show-cancel-button="false"
          :confirm-button-label="
            identityUncertain ? 'Проверить сохранение' : 'Подтвердить'
          "
          :is-loading="identitySaving"
          @confirm="confirmIdentity"
        >
          <p class="text-sm text-n-slate-11 m-0">
            Укажите постоянный ID нужного пользователя Telegram. После
            подтверждения он закрепится за размещением.
          </p>
          <Input
            v-model="identityValue"
            label="TG User ID"
            :disabled="identitySaving || identityUncertain"
          />
          <Banner v-if="identityError" color="ruby" role="alert">
            {{ identityError }}
          </Banner>
        </Dialog>
        <Dialog
          ref="successDialog"
          width="sm"
          confirm-button-label="Создать ещё"
          :show-cancel-button="false"
          @confirm="successDialog?.close()"
        >
          <img
            :src="successImage"
            alt=""
            width="325"
            height="284"
            class="block mx-auto w-full max-w-64 max-h-[35vh] object-contain"
          />
          <h3
            class="text-base font-medium leading-6 text-n-slate-12 text-center m-0"
          >
            Динарочка ты умничка 😘
          </h3>
          <p class="text-sm text-n-slate-11 text-center m-0">{{ notice }}</p>
        </Dialog>
      </template>
    </template>
  </SettingsLayout>
</template>

<style scoped>
.placements-page :deep(button:focus-visible) {
  @apply outline outline-2 outline-n-brand outline-offset-2;
}
</style>
